import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { DigestGroup, WorkDigest } from '../api/types'
import { errorMessage } from '../api/client'
import { Popover } from '../components/Popover'
import { Time } from '../components/Time'
import { useBoardState, useStore } from '../store/context'
import { actorName } from '../vocab'
import './digest.css'

function Group({ title, group }: { title: string; group: DigestGroup }) {
  if (!group.items.length) return null
  return <section className="digest-group" aria-label={title}>
    <h3>{title} <span className="muted">{group.items.length}{group.more ? '+' : ''}</span></h3>
    <ul>{group.items.map(line => <li key={line.id}>
      <div className="digest-line">
        {line.key ? <Link to={`/${encodeURIComponent(line.key)}`}>{line.key} · {line.title}</Link> : <span>{line.title}</span>}
        <Time iso={line.at} />
      </div>
      {line.detail && <p className="digest-detail">{title === 'Landed' ? line.detail.slice(0, 7) : line.detail}</p>}
      {line.actor && <span className="muted">by {actorName(line.actor)}</span>}
    </li>)}</ul>
    {group.more && <p className="muted">Showing the newest {group.items.length}.</p>}
  </section>
}

export function Digest({ digest }: { digest: WorkDigest }) {
  const groups: [string, DigestGroup][] = [['Landed', digest.landed], ['Failed', digest.failed], ['Decided', digest.decided],
    ['Filed', digest.filed], ['Investigated', digest.investigated]]
  return <div className="digest-body">
    <p className="muted">Last 24 hours · Since <Time iso={digest.since} /> · {digest.runs} {digest.runs === 1 ? 'run' : 'runs'}
      {digest.cost_usd !== null && ` · $${digest.cost_usd.toFixed(2)} reported`}</p>
    {groups.every(([, g]) => !g.items.length)
      ? <p className="muted">Nothing landed, failed, was decided, was filed or was investigated since then.</p>
      : groups.map(([title, group]) => <Group key={title} title={title} group={group} />)}
    <p className="muted">Deployed work will appear when the release map is available.</p>
  </div>
}

/**
 * The project's digest, read again as the board changes and every minute;
 * none offline. Query history, including outcomes that have left the live
 * snapshot. The Board's popover and the phone's Digest tab (Poulpe-626) read it.
 */
export function useDigest(project: string): { digest: WorkDigest | null; error: string | null; offline: boolean; retry: () => void } {
  const store = useStore()
  const { issues, entries, runs, proposals, landings, offline } = useBoardState()
  const [digest, setDigest] = useState<WorkDigest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setRetry(n => n + 1), 60_000)
    return () => clearInterval(timer)
  }, [])
  useEffect(() => {
    let current = true
    if (offline) return
    // Group bursts of feed changes into one read. Drop late answers on a
    // project change, a new read or unmount.
    const timer = setTimeout(() => {
      store.digest(project).then(value => { if (current) { setDigest(value); setError(null) } },
        err => { if (current) setError(errorMessage(err)) })
    }, 250)
    return () => { current = false; clearTimeout(timer) }
  }, [store, project, issues, entries, runs, proposals, landings, offline, retry])
  return { digest, error, offline, retry: () => setRetry(n => n + 1) }
}

export function BoardDigest({ project }: { project: string }) {
  const { digest, error, offline, retry } = useDigest(project)
  return <Popover className="board-digest" label="Work digest" button="Digest" buttonTitle="Last 24 hours" align="end">
    {() => offline ? <p className="muted">Connect to read the digest.</p>
      : error ? <p role="status">{error} <button className="button" onClick={retry}>Try again</button></p>
        : digest ? <Digest digest={digest} /> : <p className="muted">Reading the digest…</p>}
  </Popover>
}
